/**
 * CV Edit Dashboard component for ClarCV application.
 * 
 * Provides an interactive dashboard where users can analyze and edit
 * each section of their CV before generating the final document.
 * 
 * @component
 * @returns {JSX.Element} CV Edit Dashboard component
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import TemplateGallery from '../components/TemplateGallery';
import TemplateRail from '../components/TemplateRail';
import OnTemplateEditor, {
  DEMO_CV_HTML,
  prepareEditableHtml,
} from '../components/OnTemplateEditor';
import FeedbackModal from '../components/FeedbackModal';
import Seo from '../components/Seo';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useRequireAuth } from '../contexts/AuthGateContext';
import { buildCvDownloadFilename } from '../utils/downloadFilename';
import { getTemplateDisplayName } from '../utils/templateNames';
import { pickDefaultTemplate, storeTemplateId } from '../utils/templateDefault';
import {
  trackCvDownload,
  trackCvPreview,
  trackGaEvent,
  trackTemplateAutoApplied,
  trackTemplateSelected,
  trackTemplatePromptOpen,
  trackTemplatePromptGenerated,
} from '../utils/analytics';
import { getErrorPayload, isPlanLimitPayload } from '../utils/planLimit';
import {
  hasTranslatableContent,
  resolveCvDocumentLanguage,
  stripPhoto,
} from '../utils/cvLanguage';
import {
  FEEDBACK_AFTER_DOWNLOAD_DELAY_MS,
  hasSubmittedFeedback,
  markFeedbackDismissed,
  shouldPromptAfterDownload,
} from '../utils/feedbackPrompt';
import { usePlanLimitPrompt } from '../hooks/usePlanLimitPrompt';
import { moveArrayItem } from '../utils/listOrder';
import {
  extractContentFromEditor,
  syncedContentEqual,
} from '../utils/cvFieldSync';
import './CVEditDashboard.css';

const LOOKS_COLLAPSED_KEY = 'clarcv.studioLooksCollapsed';

/**
 * Whether the looks rail starts collapsed (more room to edit).
 *
 * @returns {boolean}
 */
const readLooksCollapsed = () => {
  if (typeof window === 'undefined') {
    return true;
  }
  try {
    const stored = window.sessionStorage.getItem(LOOKS_COLLAPSED_KEY);
    if (stored === '0') {
      return false;
    }
    if (stored === '1') {
      return true;
    }
  } catch (err) {
    return true;
  }
  return true;
};

/**
 * Remember looks rail collapsed/expanded for this tab.
 *
 * @param {boolean} collapsed
 */
const persistLooksCollapsed = (collapsed) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(LOOKS_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch (err) {
    // Private mode / quota — studio still works without persistence.
  }
};

const A4_PREVIEW_WIDTH = 794;
const A4_PREVIEW_HEIGHT = 1123;
const PREVIEW_ZOOM_STEPS = [1, 1.25, 1.5, 2];

/**
 * Renders filled template HTML at A4 size. Default fits the stage; zoom
 * enlarges the page so the visitor can read small type.
 */
const ScaledCvFrame = ({
  src,
  title,
  zoomInLabel,
  zoomOutLabel,
  zoomFitLabel,
}) => {
  const hostRef = useRef(null);
  const [fitScale, setFitScale] = useState(1);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return undefined;
    }
    const measure = () => {
      const width = host.clientWidth;
      const height = host.clientHeight;
      if (!width) {
        return;
      }
      const byWidth = width / A4_PREVIEW_WIDTH;
      const byHeight = height > 40 ? height / A4_PREVIEW_HEIGHT : byWidth;
      setFitScale(Math.min(byWidth, byHeight, 1));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, [src]);

  useEffect(() => {
    if (zoom != null && zoom <= fitScale + 0.02) {
      setZoom(null);
    }
  }, [fitScale, zoom]);

  const isFit = zoom == null;
  const scale = isFit ? fitScale : zoom;

  const zoomIn = () => {
    setZoom((current) => {
      const actual = current == null ? fitScale : current;
      return (
        PREVIEW_ZOOM_STEPS.find((step) => step > actual + 0.02)
        || PREVIEW_ZOOM_STEPS[PREVIEW_ZOOM_STEPS.length - 1]
      );
    });
  };

  const zoomOut = () => {
    setZoom((current) => {
      const actual = current == null ? fitScale : current;
      const lower = PREVIEW_ZOOM_STEPS.filter((step) => step < actual - 0.02);
      const prev = lower[lower.length - 1];
      if (prev == null || prev <= fitScale + 0.02) {
        return null;
      }
      return prev;
    });
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return undefined;
    }
    const onWheel = (event) => {
      if (!event.ctrlKey && !event.metaKey) {
        return;
      }
      event.preventDefault();
      if (event.deltaY < 0) {
        zoomIn();
      } else {
        zoomOut();
      }
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => host.removeEventListener('wheel', onWheel);
  }, [fitScale]);

  const canZoomOut = !isFit;
  const canZoomIn = scale < PREVIEW_ZOOM_STEPS[PREVIEW_ZOOM_STEPS.length - 1] - 0.02;

  return (
    <div className="cv-edit__preview">
      <div
        className={`cv-edit__page cv-edit__page--fill${isFit ? '' : ' is-zoomed'}`}
        ref={hostRef}
      >
        <div
          className="cv-edit__page-canvas"
          style={{
            width: A4_PREVIEW_WIDTH * scale,
            height: A4_PREVIEW_HEIGHT * scale,
          }}
        >
          <iframe
            title={title}
            src={src}
            className="cv-edit__iframe"
            sandbox="allow-same-origin"
            style={{
              width: A4_PREVIEW_WIDTH,
              height: A4_PREVIEW_HEIGHT,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          />
        </div>
      </div>
      <div className="cv-edit__zoom" role="group" aria-label={zoomFitLabel}>
        <button
          type="button"
          className="cv-edit__zoom-btn"
          onClick={zoomOut}
          disabled={!canZoomOut}
          aria-label={zoomOutLabel}
        >
          −
        </button>
        <button
          type="button"
          className="cv-edit__zoom-level"
          onClick={() => setZoom(null)}
          disabled={isFit}
          title={zoomFitLabel}
        >
          {isFit ? zoomFitLabel : `${Math.round(scale * 100)}%`}
        </button>
        <button
          type="button"
          className="cv-edit__zoom-btn"
          onClick={zoomIn}
          disabled={!canZoomIn}
          aria-label={zoomInLabel}
        >
          +
        </button>
      </div>
    </div>
  );
};

ScaledCvFrame.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  zoomInLabel: PropTypes.string.isRequired,
  zoomOutLabel: PropTypes.string.isRequired,
  zoomFitLabel: PropTypes.string.isRequired,
};

/**
 * CV Edit Dashboard component.
 * 
 * @returns {JSX.Element} The CV editing interface with section-by-section editing
 */
const CVEditDashboard = () => {
  const location = useLocation();
  const { t, lang } = useLanguage();
  const { isAuthenticated, isPro } = useAuth();
  const requireAuth = useRequireAuth();
  
  // Get analysis data from location state or fetch it
  const [analysisData] = useState(location.state?.analysisData || null);
  const productFlow = analysisData ? 'ai_cv' : 'from_scratch';
  const [editableContent, setEditableContent] = useState(null);
  const [activeSection, setActiveSection] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customHtml, setCustomHtml] = useState(null);
  const [showDesignPrompt, setShowDesignPrompt] = useState(false);
  const [designPrompt, setDesignPrompt] = useState('');
  const [generatingDesign, setGeneratingDesign] = useState(false);
  const { showPlanLimit, prompt: upgradePrompt, open: upgradeOpen } =
    usePlanLimitPrompt();
  const [showFeedback, setShowFeedback] = useState(false);
  const [downloadedThisSession, setDownloadedThisSession] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(
    () => Boolean(location.state?.openGallery)
  );
  const [templates, setTemplates] = useState([]);
  const [templatePreviewUrl, setTemplatePreviewUrl] = useState(null);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [studioTab, setStudioTab] = useState('preview');
  const [stageMode, setStageMode] = useState('preview');
  const [onPageHtml, setOnPageHtml] = useState(DEMO_CV_HTML);
  const [looksCollapsed, setLooksCollapsed] = useState(readLooksCollapsed);
  // Follow the uploaded file: English PDF → EN, otherwise ES (not the website language).
  const [cvLanguage, setCvLanguage] = useState(() =>
    resolveCvDocumentLanguage({
      originalText:
        location.state?.analysisData?.original_text
        || location.state?.analysisData?.parsed_content
        || '',
      content:
        location.state?.analysisData?.final_design
        || location.state?.analysisData?.extracted_and_improved
        || location.state?.analysisData?.extracted_content
        || null,
      hinted: location.state?.analysisData?.cv_language,
      fallback: location.state?.analysisData ? 'es' : (lang === 'en' ? 'en' : 'es'),
    })
  );
  const [localizingCv, setLocalizingCv] = useState(false);
  const previewUrlRef = useRef(null);
  const previewTrackedForRef = useRef(null);
  const autoAppliedRef = useRef(false);
  const onPageEditorRef = useRef(null);
  const onPageLoadedUrlRef = useRef(null);
  const onPageTemplateKeyRef = useRef(null);
  const syncFromCanvasRef = useRef(false);
  const designPromptRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  const upgradeOpenRef = useRef(false);
  const contentByLangRef = useRef({ es: null, en: null });
  const localizedToRef = useRef({ es: false, en: false });
  const applyingLocalizedRef = useRef(false);
  const cvLanguageRef = useRef(cvLanguage);

  upgradeOpenRef.current = upgradeOpen;
  cvLanguageRef.current = cvLanguage;

  useEffect(() => {
    return () => {
      window.clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (showDesignPrompt) {
      designPromptRef.current?.focus();
    }
  }, [showDesignPrompt]);

  useEffect(() => {
    if (!isAuthenticated || isPro) {
      return undefined;
    }
    let cancelled = false;
    axios
      .get('/api/cvs/stats/')
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        const locUsed = Number(data?.quota?.localizations?.used || 0);
        const locLimit = data?.quota?.localizations?.limit;
        if (locLimit != null && locUsed >= Number(locLimit)) {
          setUsedFreeLocalize(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isPro]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('design') !== '1') {
      return;
    }
    const prompt = (params.get('prompt') || '').trim();
    if (prompt) {
      setDesignPrompt(prompt.slice(0, 2000));
    }
    setShowDesignPrompt(true);
  }, [location.search]);

  useEffect(() => {
    // When coming from "Crear CV desde cero" there is no analysisData; initialize empty content.
    // When coming from QuickEdit we have analysisData; initialize from it.
    initializeEditableContent();
    // initializeEditableContent is recreated each render; analysisData is the real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisData]);

  const previewSignature = useMemo(() => {
    if (!editableContent) {
      return '';
    }
    const { photo, ...rest } = editableContent;
    const photoKey =
      photo instanceof File
        ? `${photo.name}:${photo.size}:${photo.lastModified}`
        : typeof photo === 'string'
          ? photo
          : '';
    return `${JSON.stringify(rest)}|${photoKey}`;
  }, [editableContent]);

  useEffect(() => {
    if (!editableContent) {
      return undefined;
    }
    if (applyingLocalizedRef.current) {
      applyingLocalizedRef.current = false;
      return undefined;
    }
    const other = cvLanguageRef.current === 'es' ? 'en' : 'es';
    contentByLangRef.current[other] = null;
    localizedToRef.current[other] = false;
    return undefined;
  }, [previewSignature, editableContent]);

  useEffect(() => {
    let cancelled = false;
    const loadTemplates = async () => {
      try {
        const response = await axios.get('/api/cvs/templates/');
        if (cancelled) {
          return;
        }
        const list = Array.isArray(response.data) ? response.data : [];
        setTemplates(list);
        const preferred =
          location.state?.preferredTemplateId || location.state?.preferredTemplateName;
        const picked = pickDefaultTemplate(list, preferred);
        if (picked) {
          setSelectedTemplate((current) => {
            if (current) {
              return current;
            }
            storeTemplateId(picked.id);
            if (!autoAppliedRef.current) {
              autoAppliedRef.current = true;
              trackTemplateAutoApplied(picked);
            }
            return picked;
          });
        }
      } catch (error) {
        if (error.response?.status !== 401 && error.response?.status !== 403) {
          console.error('Error fetching templates:', error);
        }
      }
    };
    loadTemplates();
    return () => {
      cancelled = true;
    };
  }, [location.state?.preferredTemplateId, location.state?.preferredTemplateName]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  /**
   * Debounced live HTML preview of the selected template.
   * HTML fill only — not an LLM call. Guests see the thumbnail until they sign in.
   * Signed-in users wait on a spinner; sample template thumbnails are never shown as the live preview.
   */
  useEffect(() => {
    if ((!selectedTemplate && !customHtml) || !editableContent || !isAuthenticated) {
      return undefined;
    }
    const controller = new AbortController();
    const delayMs = previewUrlRef.current ? 700 : 0;
    setTemplatePreviewLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const formData = new FormData();
        formData.append(
          'analysis_data',
          JSON.stringify({
            ...editableContent,
            photo:
              editableContent.photo instanceof File ? undefined : editableContent.photo,
            original_text: analysisData?.original_text || analysisData?.parsed_content || '',
          })
        );
        if (customHtml) {
          formData.append('custom_html', customHtml);
        } else {
          formData.append('template_id', selectedTemplate.id);
        }
        formData.append('output_format', 'pdf');
        formData.append('preview', 'true');
        formData.append('cv_language', cvLanguage);
        formData.append('product_flow', productFlow);
        if (editableContent.photo instanceof File) {
          formData.append('photo', editableContent.photo);
        }
        const response = await axios.post('/api/cvs/apply-template/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob',
          signal: controller.signal,
        });
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        if (previewUrlRef.current) {
          window.URL.revokeObjectURL(previewUrlRef.current);
        }
        previewUrlRef.current = url;
        setTemplatePreviewUrl(url);
        const lookKey = customHtml ? 'ai-generated' : String(selectedTemplate.id);
        if (previewTrackedForRef.current !== lookKey) {
          previewTrackedForRef.current = lookKey;
          trackCvPreview(
            customHtml
              ? { id: 'ai-generated', name: 'AI Generated Template' }
              : selectedTemplate
          );
        }
      } catch (error) {
        if (error.code !== 'ERR_CANCELED' && error.name !== 'CanceledError') {
          console.error('Error generating template preview:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setTemplatePreviewLoading(false);
        }
      }
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [
    selectedTemplate,
    customHtml,
    previewSignature,
    cvLanguage,
    isAuthenticated,
    analysisData?.original_text,
    analysisData?.parsed_content,
    editableContent,
  ]);

  const onPageTemplateKey = customHtml
    ? 'custom'
    : String(selectedTemplate?.id || '');

  /**
   * Load filled template HTML into the on-page editor when entering the mode
   * or when the template changes — not on every form keystroke.
   */
  useEffect(() => {
    if (stageMode !== 'onpage') {
      onPageLoadedUrlRef.current = null;
      onPageTemplateKeyRef.current = null;
      return undefined;
    }
    if (!templatePreviewUrl) {
      setOnPageHtml(DEMO_CV_HTML);
      onPageLoadedUrlRef.current = null;
      return undefined;
    }
    const templateChanged = onPageTemplateKeyRef.current !== onPageTemplateKey;
    if (!templateChanged && onPageLoadedUrlRef.current) {
      onPageLoadedUrlRef.current = templatePreviewUrl;
      return undefined;
    }
    let cancelled = false;
    fetch(templatePreviewUrl)
      .then((response) => response.text())
      .then((raw) => {
        if (!cancelled) {
          setOnPageHtml(prepareEditableHtml(raw));
          onPageLoadedUrlRef.current = templatePreviewUrl;
          onPageTemplateKeyRef.current = onPageTemplateKey;
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOnPageHtml(DEMO_CV_HTML);
          onPageLoadedUrlRef.current = null;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [stageMode, templatePreviewUrl, onPageTemplateKey]);

  /**
   * Form → canvas: patch data-field nodes while staying in on-template mode.
   */
  useEffect(() => {
    if (stageMode !== 'onpage') {
      return;
    }
    if (syncFromCanvasRef.current) {
      syncFromCanvasRef.current = false;
      return;
    }
    if (!onPageLoadedUrlRef.current) {
      return;
    }
    onPageEditorRef.current?.patchFromContent?.(editableContent);
  }, [editableContent, stageMode]);

  /**
   * Canvas → form: merge [data-field] text into editableContent.
   *
   * @param {HTMLElement} root
   */
  const handleOnPageContentChange = (root) => {
    syncFromCanvasRef.current = true;
    setEditableContent((prev) => {
      const next = extractContentFromEditor(root, prev || {});
      if (syncedContentEqual(next, prev)) {
        syncFromCanvasRef.current = false;
        return prev;
      }
      return next;
    });
  };

  /** Empty content structure for "Create from scratch" */
  const getEmptyContent = () => ({
    name: '',
    title: '',
    email: '',
    phone: '',
    location: '',
    linkedin: '',
    website: '',
    github: '',
    photo: null,
    summary: '',
    experience: [],
    skills: [],
    education: [],
    languages: [],
    certifications: [],
    achievements: [],
  });

  /**
   * Initialize editable content from analysis data or empty for "from scratch".
   */
  const initializeEditableContent = () => {
    if (!analysisData) {
      const empty = getEmptyContent();
      applyingLocalizedRef.current = true;
      setEditableContent(empty);
      const fallback = lang === 'en' ? 'en' : 'es';
      setCvLanguage(fallback);
      contentByLangRef.current = { es: null, en: null };
      localizedToRef.current = { es: false, en: false };
      return;
    }
    // Extract improved content from analysis data
    const improvedContent =
      analysisData.final_design ||
      analysisData.extracted_and_improved ||
      analysisData.extracted_content ||
      {};

    const next = {
      name: improvedContent.name || '',
      title: improvedContent.title || '',
      email: improvedContent.email || '',
      phone: improvedContent.phone || '',
      location: improvedContent.location || '',
      linkedin: improvedContent.linkedin || '',
      website: improvedContent.website || '',
      github: improvedContent.github || '',
      photo: improvedContent.photo || analysisData.photo || null,
      summary: improvedContent.summary || improvedContent.professional_summary || '',
      experience: improvedContent.experience || improvedContent.work_experience || [],
      skills: improvedContent.skills || [],
      education: Array.isArray(improvedContent.education)
        ? improvedContent.education
        : (improvedContent.education
          ? [{ degree: improvedContent.education, institution: '', date: '', location: '', description: '' }]
          : []),
      languages: improvedContent.languages || [],
      certifications: improvedContent.certifications || [],
      achievements: improvedContent.achievements || [],
    };
    applyingLocalizedRef.current = true;
    setEditableContent(next);
    const detected = resolveCvDocumentLanguage({
      originalText: analysisData.original_text || analysisData.parsed_content || '',
      content: next,
      hinted: analysisData.cv_language,
      fallback: 'es',
    });
    setCvLanguage(detected);
    contentByLangRef.current = { es: null, en: null };
    contentByLangRef.current[detected] = stripPhoto(next);
    localizedToRef.current = { es: false, en: false };
    localizedToRef.current[detected] = true;
  };

  /**
   * Update a specific field in editable content.
   * 
   * @param {string} field - Field name to update
   * @param {*} value - New value
   */
  const updateField = (field, value) => {
    setEditableContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Update experience entry.
   * 
   * @param {number} index - Experience index
   * @param {string} field - Field to update
   * @param {*} value - New value
   */
  const updateExperience = (index, field, value) => {
    setEditableContent(prev => {
      const newExperience = [...prev.experience];
      newExperience[index] = {
        ...newExperience[index],
        [field]: value
      };
      return {
        ...prev,
        experience: newExperience
      };
    });
  };

  /**
   * Add new experience entry.
   */
  const addExperience = () => {
    setEditableContent(prev => ({
      ...prev,
      experience: [
        ...(prev.experience || []),
        {
          title: '',
          company: '',
          duration: '',
          description: '',
          location: ''
        }
      ]
    }));
  };

  /**
   * Remove experience entry.
   * 
   * @param {number} index - Experience index to remove
   */
  const removeExperience = (index) => {
    setEditableContent(prev => ({
      ...prev,
      experience: (prev.experience || []).filter((_, i) => i !== index)
    }));
  };

  /**
   * Reorder an experience entry.
   *
   * @param {number} fromIndex - Current index
   * @param {number} toIndex - Target index
   */
  const moveExperience = (fromIndex, toIndex) => {
    setEditableContent(prev => ({
      ...prev,
      experience: moveArrayItem(prev.experience || [], fromIndex, toIndex)
    }));
  };

  /**
   * Update education entry.
   * 
   * @param {number} index - Education index
   * @param {string} field - Field to update
   * @param {*} value - New value
   */
  const updateEducation = (index, field, value) => {
    setEditableContent(prev => {
      const newEducation = [...(prev.education || [])];
      newEducation[index] = {
        ...newEducation[index],
        [field]: value
      };
      return {
        ...prev,
        education: newEducation
      };
    });
  };

  /**
   * Add new education entry.
   */
  const addEducation = () => {
    setEditableContent(prev => ({
      ...prev,
      education: [
        ...(prev.education || []),
        {
          degree: '',
          institution: '',
          date: '',
          location: '',
          description: ''
        }
      ]
    }));
  };

  /**
   * Remove education entry.
   * 
   * @param {number} index - Education index to remove
   */
  const removeEducation = (index) => {
    setEditableContent(prev => ({
      ...prev,
      education: (prev.education || []).filter((_, i) => i !== index)
    }));
  };

  /**
   * Reorder an education entry.
   *
   * @param {number} fromIndex - Current index
   * @param {number} toIndex - Target index
   */
  const moveEducation = (fromIndex, toIndex) => {
    setEditableContent(prev => ({
      ...prev,
      education: moveArrayItem(prev.education || [], fromIndex, toIndex)
    }));
  };

  /**
   * Update skills array.
   * 
   * @param {Array} newSkills - New skills array
   */
  const updateSkills = (newSkills) => {
    setEditableContent(prev => ({
      ...prev,
      skills: newSkills
    }));
  };

  /**
   * Update languages array.
   * 
   * @param {Array} newLanguages - New languages array
   */
  const updateLanguages = (newLanguages) => {
    setEditableContent(prev => ({
      ...prev,
      languages: newLanguages
    }));
  };

  /**
   * Update certifications array.
   * 
   * @param {Array} newCertifications - New certifications array
   */
  const updateCertifications = (newCertifications) => {
    setEditableContent(prev => ({
      ...prev,
      certifications: newCertifications
    }));
  };

  /**
   * After a successful PDF/Word download, offer feedback once (delayed so the
   * toast is not competing) and keep a quiet toolbar link for this session.
   *
   * @returns {void}
   */
  const scheduleAfterDownloadFeedback = () => {
    setDownloadedThisSession(true);
    if (!isAuthenticated || !shouldPromptAfterDownload()) {
      return;
    }
    window.clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = window.setTimeout(() => {
      if (upgradeOpenRef.current) {
        return;
      }
      setShowFeedback(true);
      trackGaEvent('feedback_prompt_shown', { source: 'after_download' });
    }, FEEDBACK_AFTER_DOWNLOAD_DELAY_MS);
  };

  /**
   * Switch CV language: labels always; body via AI (free). Download stays Pro.
   *
   * @param {'es'|'en'} target
   * @returns {Promise<void>}
   */
  const handleCvLanguageChange = async (target) => {
    if (target === cvLanguage || localizingCv) {
      return;
    }
    if (!editableContent) {
      setCvLanguage(target);
      return;
    }

    contentByLangRef.current[cvLanguage] = stripPhoto(editableContent);
    localizedToRef.current[cvLanguage] = true;
    const cached = contentByLangRef.current[target];
    if (cached && localizedToRef.current[target]) {
      applyingLocalizedRef.current = true;
      setEditableContent({ ...cached, photo: editableContent.photo });
      setCvLanguage(target);
      return;
    }

    if (!hasTranslatableContent(editableContent)) {
      setCvLanguage(target);
      return;
    }

    if (!(await requireAuth())) {
      return;
    }

    setLocalizingCv(true);
    try {
      const response = await axios.post('/api/cvs/localize-content/', {
        content: stripPhoto(editableContent),
        target_language: target,
        original_text: analysisData?.original_text || analysisData?.parsed_content || '',
        product_flow: productFlow,
      });
      const localized = response.data?.content;
      if (!localized || typeof localized !== 'object') {
        throw new Error('empty content');
      }
      if (response.data?.unchanged) {
        setCvLanguage(target);
        return;
      }
      const next = { ...localized, photo: editableContent.photo };
      applyingLocalizedRef.current = true;
      contentByLangRef.current[target] = stripPhoto(next);
      localizedToRef.current[target] = true;
      setEditableContent(next);
      setCvLanguage(target);
      trackGaEvent('cv_localized', {
        language: target,
        unchanged: false,
      });
    } catch (error) {
      const payload = await getErrorPayload(error);
      if (isPlanLimitPayload(payload)) {
        showPlanLimit(payload);
        return;
      }
      toast.error(payload.error || t('editCv.cvLanguageFail'));
    } finally {
      setLocalizingCv(false);
    }
  };

  /**
   * Generate CV with edited content.
   * 
   * @param {string} format - Output format ('docx' or 'pdf')
   */
  const generateCV = async (format = 'docx') => {
    if (!selectedTemplate && !customHtml) {
      toast.info(t('editCv.needTemplate'));
      setShowTemplateGallery(true);
      return;
    }
    if (!editableContent) {
      toast.error('No hay contenido para generar');
      return;
    }
    if (!(await requireAuth())) {
      return;
    }

    setSaving(true);

    try {
      if (stageMode === 'onpage') {
        onPageEditorRef.current?.flushSync?.();
      }
      // Prepare analysis data with edited content
      const updatedAnalysisData = {
        ...analysisData,
        final_design: editableContent,
        extracted_and_improved: editableContent
      };

      // If template is selected, use template endpoint
      if (selectedTemplate || customHtml) {
        // Use FormData to properly send photo file
        const formData = new FormData();
        
        // Remove photo from analysis_data before stringifying (will send as file)
        const analysisForJson = { ...updatedAnalysisData };
        if (analysisForJson.final_design) {
          analysisForJson.final_design = { ...analysisForJson.final_design };
          if (analysisForJson.final_design.photo instanceof File) {
            delete analysisForJson.final_design.photo;
          }
        }
        if (analysisForJson.extracted_and_improved) {
          analysisForJson.extracted_and_improved = { ...analysisForJson.extracted_and_improved };
          if (analysisForJson.extracted_and_improved.photo instanceof File) {
            delete analysisForJson.extracted_and_improved.photo;
          }
        }
        if (editableContent.photo instanceof File) {
          delete analysisForJson.photo;
        } else if (typeof editableContent.photo === 'string' && editableContent.photo) {
          analysisForJson.photo = editableContent.photo;
        } else {
          // User removed the photo: do not fall back to the extracted URI.
          analysisForJson.photo = null;
          if (analysisForJson.final_design) {
            analysisForJson.final_design = { ...analysisForJson.final_design, photo: null };
          }
          if (analysisForJson.extracted_and_improved) {
            analysisForJson.extracted_and_improved = {
              ...analysisForJson.extracted_and_improved,
              photo: null,
            };
          }
          if (analysisForJson.extracted_content) {
            analysisForJson.extracted_content = {
              ...analysisForJson.extracted_content,
              photo: null,
            };
          }
        }

        formData.append('analysis_data', JSON.stringify(analysisForJson));
        if (customHtml) {
          formData.append('custom_html', customHtml);
        } else {
          formData.append('template_id', selectedTemplate.id);
        }
        formData.append('output_format', format);
        formData.append('cv_language', cvLanguage);
        formData.append('product_flow', productFlow);
        formData.append('fit_to_one_page', 'true');

        // On-template mode: export the live canvas (includes clicks/formatting).
        if (stageMode === 'onpage') {
          const prefilled = onPageEditorRef.current?.getExportHtml?.() || '';
          if (prefilled.trim().length > 40) {
            formData.append('prefilled_html', prefilled);
          }
        }
        
        // Add photo if it's a File object
        if (editableContent.photo && editableContent.photo instanceof File) {
          formData.append('photo', editableContent.photo);
        }
        
        const response = await axios.post(
          '/api/cvs/apply-template/',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            responseType: 'blob',
          }
        );

        // Verify response is valid
        if (!response.data || response.data.size === 0) {
          throw new Error('Archivo vacío recibido del servidor');
        }

        // Check if response is an error (JSON instead of blob)
        if (response.data.type && response.data.type.includes('application/json')) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              throw new Error(errorData.error || 'Error al generar el CV');
            } catch (e) {
              throw new Error('Error al generar el CV');
            }
          };
          reader.readAsText(response.data);
          return;
        }

        // Create download link
        const blob = new Blob([response.data], { 
          type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = buildCvDownloadFilename({
          name: editableContent.name,
          title: editableContent.title,
          extension: format,
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.success(
          t('editCv.generatedOk').replace(
            '{name}',
            customHtml ? t('editCv.aiLookName') : selectedTemplate.name
          )
        );
        trackCvDownload({
          format,
          template: customHtml
            ? { id: 'ai-generated', name: 'AI Generated Template' }
            : selectedTemplate,
        });
        scheduleAfterDownloadFeedback();
      } else {
        // Use regular generation without template
        const formData = new FormData();
        formData.append('analysis_data', JSON.stringify(updatedAnalysisData));
        formData.append('output_format', format);
        formData.append('format_mode', 'free'); // Use free mode for edited content
        formData.append('product_flow', productFlow);
        
        // Add photo if it's a File object
        if (editableContent.photo && editableContent.photo instanceof File) {
          formData.append('photo', editableContent.photo);
        }

        const response = await axios.post('/api/cvs/generate-improved-cv/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'blob'
        });

        // Verify response is valid
        if (!response.data || response.data.size === 0) {
          throw new Error('Archivo vacío recibido del servidor');
        }

        // Check if response is an error (JSON instead of blob)
        if (response.data.type && response.data.type.includes('application/json')) {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              throw new Error(errorData.error || 'Error al generar el CV');
            } catch (e) {
              throw new Error('Error al generar el CV');
            }
          };
          reader.readAsText(response.data);
          return;
        }

        // Create download link
        const blob = new Blob([response.data], { 
          type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = buildCvDownloadFilename({
          name: editableContent.name,
          title: editableContent.title,
          extension: format,
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.success(`CV generado correctamente en formato ${format.toUpperCase()}`);
        scheduleAfterDownloadFeedback();
      }
    } catch (error) {
      console.error('Error generating CV:', error);
      const payload = await getErrorPayload(error);
      if (isPlanLimitPayload(payload)) {
        showPlanLimit(payload);
        return;
      }
      
      // Try to extract error message from blob response if it's JSON
      if (error.response && error.response.data instanceof Blob) {
        error.response.data.text().then(text => {
          try {
            const errorData = JSON.parse(text);
            toast.error(errorData.error || 'Error al generar el CV');
          } catch (e) {
            toast.error('Error al generar el CV. Inténtalo de nuevo.');
          }
        }).catch(() => {
          toast.error('Error al generar el CV. Inténtalo de nuevo.');
        });
      } else {
        const errorMessage = error.response?.data?.error || 
                            error.message || 
                            'Error al generar el CV. Inténtalo de nuevo.';
        toast.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  const setLooksOpen = (open) => {
    setLooksCollapsed(!open);
    persistLooksCollapsed(!open);
  };

  /**
   * Handle template selection from gallery or the looks rail.
   *
   * @param {Object} template - Selected template
   */
  const handleTemplateSelect = (template, { auto = false } = {}) => {
    setCustomHtml(null);
    setShowDesignPrompt(false);
    setSelectedTemplate(template);
    storeTemplateId(template.id);
    setShowTemplateGallery(false);
    if (auto) {
      return;
    }
    setLooksOpen(false);
    trackTemplateSelected(template);
  };

  const handleOpenDesignPrompt = () => {
    if (!showDesignPrompt) {
      trackTemplatePromptOpen();
      setLooksOpen(true);
    }
    setShowDesignPrompt((open) => !open);
  };

  const handleGenerateLook = async (event) => {
    event.preventDefault();
    const prompt = designPrompt.trim();
    if (!prompt) {
      toast.info(t('editCv.designPromptNeedText'));
      return;
    }
    if (!(await requireAuth())) {
      return;
    }
    if (!isPro) {
      showPlanLimit({ error_code: 'plan_limit_design' });
      return;
    }
    setGeneratingDesign(true);
    try {
      const payload = { prompt };
      if (customHtml) {
        payload.custom_html = customHtml;
      }
      if (selectedTemplate?.id) {
        payload.template_id = selectedTemplate.id;
      }
      const response = await axios.post('/api/cvs/customize-template/', payload);
      const html = response.data?.html;
      if (!html) {
        throw new Error('empty html');
      }
      setCustomHtml(html);
      setShowDesignPrompt(false);
      trackTemplatePromptGenerated();
      toast.success(t('editCv.designPromptOk'));
    } catch (error) {
      const payload = await getErrorPayload(error);
      if (isPlanLimitPayload(payload)) {
        showPlanLimit(payload);
        return;
      }
      toast.error(payload.error || t('editCv.designPromptFail'));
    } finally {
      setGeneratingDesign(false);
    }
  };

  if (!editableContent) {
    return (
      <div className="container cv-edit">
        <Seo title={t('editCv.title')} description={t('editCv.lead')} path="/dashboard/edit-cv" noindex />
        <div className="cv-edit__loading" role="status">
          <div className="cv-edit__spinner" aria-hidden="true" />
          <p>{t('editCv.loading')}</p>
          <Link to="/dashboard/quick-edit" className="btn btn-outline">
            {t('editCv.goUpload')}
          </Link>
        </div>
      </div>
    );
  }

  const sections = [
    { id: 'personal', label: t('editCv.personal') },
    { id: 'summary', label: t('editCv.summary') },
    { id: 'experience', label: t('editCv.experience') },
    { id: 'skills', label: t('editCv.skills') },
    { id: 'education', label: t('editCv.education') },
    { id: 'languages', label: t('editCv.languages') },
    { id: 'certifications', label: t('editCv.certifications') },
    { id: 'achievements', label: t('editCv.achievements') },
  ];

  const templateLabel = customHtml
    ? t('editCv.aiLookName')
    : selectedTemplate
      ? getTemplateDisplayName(selectedTemplate.name, lang)
      : t('editCv.chooseTemplate');
  const hasLivePreview = Boolean(templatePreviewUrl);
  const guestThumb = !isAuthenticated ? selectedTemplate?.thumbnail_url : '';
  const awaitingUserPreview =
    isAuthenticated && Boolean(selectedTemplate || customHtml) && !hasLivePreview;
  const looksHidden =
    looksCollapsed && Boolean(selectedTemplate || customHtml) && !showDesignPrompt;

  return (
    <div className="container cv-edit">
      <Seo title={t('editCv.title')} description={t('editCv.lead')} path="/dashboard/edit-cv" noindex />

      <header className="cv-edit__toolbar">
        <div className="cv-edit__toolbar-copy">
          <Link to="/dashboard" className="cv-edit__back">
            ← {t('editCv.back')}
          </Link>
          <h1 className="cv-edit__title">
            {t('editCv.title')}
            {selectedTemplate || customHtml ? (
              <span className="cv-edit__look-name"> · {templateLabel}</span>
            ) : null}
          </h1>
        </div>
        <div className="cv-edit__actions">
          <div className="cv-edit__cv-lang" role="group" aria-label={t('editCv.cvLanguage')}>
            <span className="cv-edit__cv-lang-label">{t('editCv.cvLanguage')}</span>
            <button
              type="button"
              className={`cv-edit__cv-lang-btn${cvLanguage === 'es' ? ' is-active' : ''}`}
              aria-pressed={cvLanguage === 'es'}
              disabled={localizingCv}
              onClick={() => handleCvLanguageChange('es')}
            >
              {t('editCv.cvLangEs')}
            </button>
            <button
              type="button"
              className={`cv-edit__cv-lang-btn${cvLanguage === 'en' ? ' is-active' : ''}`}
              aria-pressed={cvLanguage === 'en'}
              disabled={localizingCv}
              onClick={() => handleCvLanguageChange('en')}
            >
              {t('editCv.cvLangEn')}
            </button>
            {localizingCv ? (
              <span className="cv-edit__cv-lang-status">{t('editCv.cvLanguageWorking')}</span>
            ) : (
              <span className="cv-edit__cv-lang-hint">{t('editCv.cvLanguageHint')}</span>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => generateCV('pdf')}
            disabled={saving}
          >
            {saving ? t('editCv.generating') : t('editCv.downloadPdf')}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => generateCV('docx')}
            disabled={saving}
          >
            {saving ? t('editCv.generating') : t('editCv.downloadWord')}
          </button>
          {isAuthenticated && downloadedThisSession && !hasSubmittedFeedback() ? (
            <button
              type="button"
              className="cv-edit__feedback"
              onClick={() => {
                window.clearTimeout(feedbackTimerRef.current);
                setShowFeedback(true);
              }}
            >
              {t('feedback.afterDownloadCta')}
            </button>
          ) : null}
        </div>
      </header>

      <div className="cv-edit__tabs" role="tablist" aria-label={t('editCv.title')}>
        <button
          type="button"
          role="tab"
          aria-selected={studioTab === 'design'}
          className={`cv-edit__tab${studioTab === 'design' ? ' is-active' : ''}`}
          onClick={() => {
            setLooksOpen(true);
            setStudioTab('design');
          }}
        >
          {t('editCv.tabDesign')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={studioTab === 'preview'}
          className={`cv-edit__tab${studioTab === 'preview' ? ' is-active' : ''}`}
          onClick={() => setStudioTab('preview')}
        >
          {t('editCv.tabPreview')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={studioTab === 'edit'}
          className={`cv-edit__tab${studioTab === 'edit' ? ' is-active' : ''}`}
          onClick={() => setStudioTab('edit')}
        >
          {t('editCv.tabEdit')}
        </button>
      </div>

      {showTemplateGallery && (
        <TemplateGallery
          analysisData={{
            ...analysisData,
            final_design: editableContent,
            extracted_and_improved: editableContent,
            editableContent: editableContent,
            photo: editableContent?.photo || null
          }}
          onTemplateSelect={handleTemplateSelect}
          onClose={() => setShowTemplateGallery(false)}
          showAiTemplateOption={false}
          pickOnly
          cvLanguage={cvLanguage}
          productFlow={productFlow}
        />
      )}

      <div
        className="cv-edit__studio"
        data-tab={studioTab}
        data-looks={looksHidden ? 'collapsed' : 'open'}
      >
        <aside className={`cv-edit__looks${looksHidden ? ' is-collapsed' : ''}`}>
          {looksHidden ? (
            <div className="cv-edit__looks-compact">
              <div className="cv-edit__looks-compact-copy">
                <span className="cv-edit__looks-compact-label">{t('editCv.looks')}</span>
                <span className="cv-edit__looks-compact-name">{templateLabel}</span>
              </div>
              <div className="cv-edit__looks-compact-actions">
                <button
                  type="button"
                  className="cv-edit__looks-compact-btn"
                  onClick={() => setLooksOpen(true)}
                  aria-expanded="false"
                >
                  {t('editCv.changeTemplate')}
                </button>
                <button
                  type="button"
                  className="cv-edit__looks-compact-btn cv-edit__looks-compact-btn--ai"
                  onClick={handleOpenDesignPrompt}
                >
                  {t('editCv.designWithAi')}
                </button>
              </div>
            </div>
          ) : (
            <TemplateRail
              templates={templates}
              selectedId={selectedTemplate?.id}
              onSelect={handleTemplateSelect}
              onSeeAll={() => setShowTemplateGallery(true)}
              onDesignPrompt={handleOpenDesignPrompt}
              onHideLooks={
                selectedTemplate || customHtml ? () => setLooksOpen(false) : undefined
              }
              hideLooksLabel={t('editCv.hideLooks')}
              designPromptOpen={showDesignPrompt}
              lang={lang}
              looksLabel={t('editCv.looks')}
              looksHint={t('editCv.looksHint')}
              looksBadge={t('editCv.looksIncluded')}
              seeAllLabel={t('editCv.seeAllLooks')}
              designPromptLabel={t('editCv.designWithAi')}
              designHint={isPro ? t('editCv.designAiHintPro') : t('editCv.designAiHint')}
              proBadge={t('editCv.proBadge')}
              aiLookActive={Boolean(customHtml)}
              aiLookLabel={t('editCv.aiLookName')}
            />
          )}
          {showDesignPrompt ? (
            <form className="cv-edit__prompt" onSubmit={handleGenerateLook}>
              <div className="cv-edit__prompt-head">
                <label className="cv-edit__prompt-label" htmlFor="cv-design-prompt">
                  {t('editCv.designPromptLabel')}
                </label>
                <button
                  type="button"
                  className="cv-edit__prompt-close"
                  onClick={() => setShowDesignPrompt(false)}
                  aria-label={t('editCv.designPromptCancel')}
                >
                  ×
                </button>
              </div>
              <p className="cv-edit__prompt-lead">{t('editCv.designPromptLead')}</p>
              <textarea
                id="cv-design-prompt"
                ref={designPromptRef}
                className="cv-edit__prompt-input"
                value={designPrompt}
                onChange={(event) => setDesignPrompt(event.target.value)}
                placeholder={t('editCv.designPromptPlaceholder')}
                maxLength={2000}
                rows={3}
                disabled={generatingDesign}
              />
              <div className="cv-edit__prompt-examples" role="group" aria-label={t('editCv.designPromptExamples')}>
                {['designPromptEx1', 'designPromptEx2', 'designPromptEx3'].map((key) => (
                  <button
                    key={key}
                    type="button"
                    className="cv-edit__prompt-chip"
                    onClick={() => setDesignPrompt(t(`editCv.${key}`))}
                    disabled={generatingDesign}
                  >
                    {t(`editCv.${key}`)}
                  </button>
                ))}
              </div>
              <div className="cv-edit__prompt-row">
                <p className="cv-edit__prompt-hint">
                  {t(isPro ? 'editCv.designPromptProHint' : 'editCv.designPromptPayHint')}
                </p>
                <div className="cv-edit__prompt-actions">
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => setShowDesignPrompt(false)}
                    disabled={generatingDesign}
                  >
                    {t('editCv.designPromptCancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary cv-edit__prompt-submit"
                    disabled={generatingDesign || !designPrompt.trim()}
                  >
                    {generatingDesign
                      ? t('editCv.designPromptGenerating')
                      : isPro
                        ? t('editCv.designPromptSubmit')
                        : t('editCv.designPromptSubmitPro')}
                  </button>
                </div>
              </div>
            </form>
          ) : null}
        </aside>

        <section className="cv-edit__stage" aria-label={t('editCv.tabPreview')}>
          <div className="cv-edit__stage-modes" role="tablist" aria-label={t('editCv.stageModeLabel')}>
            <button
              type="button"
              className={`cv-edit__stage-mode${stageMode === 'preview' ? ' is-active' : ''}`}
              aria-selected={stageMode === 'preview'}
              onClick={() => {
                onPageEditorRef.current?.flushSync?.();
                setStageMode('preview');
              }}
            >
              {t('editCv.stageModePreview')}
            </button>
            <button
              type="button"
              className={`cv-edit__stage-mode${stageMode === 'onpage' ? ' is-active' : ''}`}
              aria-selected={stageMode === 'onpage'}
              onClick={() => setStageMode('onpage')}
            >
              {t('editCv.stageModeOnPage')}
            </button>
          </div>
          {generatingDesign ? (
            <p className="cv-edit__updating">{t('editCv.designPromptGenerating')}</p>
          ) : null}
          {templatePreviewLoading && hasLivePreview && stageMode === 'preview' ? (
            <p className="cv-edit__updating">{t('editCv.previewUpdating')}</p>
          ) : null}
          {stageMode === 'onpage' ? (
            <OnTemplateEditor
              ref={onPageEditorRef}
              html={onPageHtml}
              hint={t('editCv.onTemplateHint')}
              trialNote={
                hasLivePreview
                  ? t('editCv.onTemplateTrialLive')
                  : t('editCv.onTemplateTrial')
              }
              onContentChange={handleOnPageContentChange}
            />
          ) : hasLivePreview ? (
            <ScaledCvFrame
              src={templatePreviewUrl}
              title={templateLabel}
              zoomInLabel={t('editCv.zoomIn')}
              zoomOutLabel={t('editCv.zoomOut')}
              zoomFitLabel={t('editCv.zoomFit')}
            />
          ) : guestThumb ? (
            <div className="cv-edit__page cv-edit__page--shot">
              <img
                src={guestThumb}
                alt={templateLabel}
                className="cv-edit__shot"
              />
              <p className="cv-edit__stage-hint">{t('editCv.previewNeedAuth')}</p>
            </div>
          ) : templatePreviewLoading || awaitingUserPreview ? (
            <div className="cv-edit__stage-empty" role="status">
              <div className="cv-edit__spinner" aria-hidden="true" />
              <p>{t('editCv.previewPreparing')}</p>
            </div>
          ) : (
            <div className="cv-edit__page cv-edit__page--generic">
              <PreviewPanel content={editableContent} />
            </div>
          )}
        </section>

        <aside className="cv-edit__inspector">
          <nav className="cv-edit__nav" aria-label={t('editCv.title')}>
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`cv-edit__nav-btn${activeSection === section.id ? ' is-active' : ''}`}
                onClick={() => setActiveSection(section.id)}
                aria-current={activeSection === section.id ? 'true' : undefined}
              >
                {section.label}
              </button>
            ))}
          </nav>
          <div className="cv-edit__panel">
            <EditPanel
              section={activeSection}
              content={editableContent}
              updateField={updateField}
              updateExperience={updateExperience}
              addExperience={addExperience}
              removeExperience={removeExperience}
              moveExperience={moveExperience}
              updateEducation={updateEducation}
              addEducation={addEducation}
              removeEducation={removeEducation}
              moveEducation={moveEducation}
              updateSkills={updateSkills}
              updateLanguages={updateLanguages}
              updateCertifications={updateCertifications}
            />
          </div>
        </aside>
      </div>

      {upgradePrompt}
      <FeedbackModal
        open={showFeedback}
        variant="afterDownload"
        page="/dashboard/edit-cv"
        onClose={() => {
          setShowFeedback(false);
          markFeedbackDismissed();
        }}
      />
    </div>
  );
};

/**
 * Edit Panel component for editing CV sections.
 */
const EditPanel = ({
  section,
  content,
  updateField,
  updateExperience,
  addExperience,
  removeExperience,
  moveExperience,
  updateEducation,
  addEducation,
  removeEducation,
  moveEducation,
  updateSkills,
  updateLanguages,
  updateCertifications
}) => {
  switch (section) {
    case 'personal':
      return <PersonalInfoEditor content={content} updateField={updateField} />;
    case 'summary':
      return <SummaryEditor content={content} updateField={updateField} />;
    case 'experience':
      return (
        <ExperienceEditor
          experience={content.experience}
          updateExperience={updateExperience}
          addExperience={addExperience}
          removeExperience={removeExperience}
          moveExperience={moveExperience}
        />
      );
    case 'skills':
      return <SkillsEditor skills={content.skills} updateSkills={updateSkills} />;
    case 'education':
      return (
        <EducationEditor
          education={content.education}
          updateEducation={updateEducation}
          addEducation={addEducation}
          removeEducation={removeEducation}
          moveEducation={moveEducation}
        />
      );
    case 'languages':
      return <LanguagesEditor languages={content.languages} updateLanguages={updateLanguages} />;
    case 'certifications':
      return (
        <CertificationsEditor
          certifications={content.certifications}
          updateCertifications={updateCertifications}
        />
      );
    case 'achievements':
      return <AchievementsEditor achievements={content.achievements} updateField={updateField} />;
    default:
      return <div>Elige una sección para editar</div>;
  }
};

/**
 * Resolve a photo value (File | string | null) to a displayable src string.
 * Returns null if unresolvable.
 *
 * @param {File|string|null} photo
 * @param {string|null} objectUrl - pre-created object URL for File instances
 * @returns {string|null}
 */
const resolvePhotoSrc = (photo, objectUrl) => {
  if (!photo) return null;
  if (photo instanceof File) return objectUrl;
  if (typeof photo !== 'string') return null;
  if (
    photo.startsWith('http://') ||
    photo.startsWith('https://') ||
    photo.startsWith('data:')
  ) {
    return photo;
  }
  if (photo.startsWith('/media/')) {
    const base =
      axios.defaults.baseURL ||
      process.env.REACT_APP_API_URL ||
      'http://localhost:8000';
    return `${base}${photo}`;
  }
  return photo.startsWith('/') ? photo : `/${photo}`;
};

/**
 * Personal Information Editor component.
 */
const PersonalInfoEditor = ({ content, updateField }) => {
  const { t } = useLanguage();
  // Stable object URL for the current File photo — revoked on change / unmount.
  const objectUrlRef = useRef(null);
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (content.photo instanceof File) {
      const url = URL.createObjectURL(content.photo);
      objectUrlRef.current = url;
      setObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        objectUrlRef.current = null;
      };
    }
    // Not a File — revoke any stale URL
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);
    return undefined;
  }, [content.photo]);

  const photoSrc = resolvePhotoSrc(content.photo, objectUrl);

  return (
    <div className="edit-section">
      <h2>{t('editCv.personal')}</h2>
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="cv-name">{t('editCv.fieldName')} *</label>
          <input
            id="cv-name"
            type="text"
            value={content.name || ''}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder={t('editCv.placeholderName')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cv-title">{t('editCv.fieldTitle')} *</label>
          <input
            id="cv-title"
            type="text"
            value={content.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
            placeholder={t('editCv.placeholderTitle')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cv-email">{t('editCv.fieldEmail')}</label>
          <input
            id="cv-email"
            type="email"
            value={content.email || ''}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder={t('editCv.placeholderEmail')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cv-phone">{t('editCv.fieldPhone')}</label>
          <input
            id="cv-phone"
            type="tel"
            value={content.phone || ''}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder={t('editCv.placeholderPhone')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cv-location">{t('editCv.fieldLocation')}</label>
          <input
            id="cv-location"
            type="text"
            value={content.location || ''}
            onChange={(e) => updateField('location', e.target.value)}
            placeholder={t('editCv.placeholderLocation')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cv-linkedin">LinkedIn (URL)</label>
          <input
            id="cv-linkedin"
            type="url"
            value={content.linkedin || ''}
            onChange={(e) => updateField('linkedin', e.target.value)}
            placeholder={t('editCv.placeholderLinkedin')}
          />
        </div>
        <div className="form-group">
          <label htmlFor="cv-website">{t('editCv.fieldWebsite')}</label>
          <input
            id="cv-website"
            type="url"
            value={content.website || ''}
            onChange={(e) => updateField('website', e.target.value)}
            placeholder="https://tu-web.com"
          />
        </div>
        <div className="form-group">
          <label htmlFor="cv-github">GitHub (URL)</label>
          <input
            id="cv-github"
            type="url"
            value={content.github || ''}
            onChange={(e) => updateField('github', e.target.value)}
            placeholder="github.com/tu-usuario"
          />
        </div>
      </div>

      {/* Photo Upload */}
      <div className="form-group" style={{ marginTop: '1.5rem' }}>
        <label htmlFor="cv-photo">{t('editCv.fieldPhoto')}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {photoSrc ? (
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '8px',
              overflow: 'hidden',
              border: '2px solid var(--color-border)',
              flexShrink: 0,
            }}>
              <img
                src={photoSrc}
                alt={t('editCv.fieldPhoto')}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          ) : null}
          <div style={{ flex: 1 }}>
            <input
              id="cv-photo"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 2 * 1024 * 1024) {
                  toast.error(t('editCv.photoTooBig'));
                  return;
                }
                if (!file.type.startsWith('image/')) {
                  toast.error(t('editCv.photoInvalidType'));
                  return;
                }
                updateField('photo', file);
              }}
              style={{ display: 'none' }}
            />
            <label htmlFor="cv-photo" className="btn btn-primary" style={{ display: 'inline-block', cursor: 'pointer' }}>
              {content.photo ? t('editCv.changePhoto') : t('editCv.uploadPhoto')}
            </label>
            {content.photo ? (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => updateField('photo', null)}
                style={{ marginLeft: '0.5rem' }}
              >
                {t('editCv.removePhoto')}
              </button>
            ) : null}
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-muted)' }}>
              {t('editCv.photoHint')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Summary Editor component.
 */
const SummaryEditor = ({ content, updateField }) => {
  const { t } = useLanguage();
  const summary = content.summary || '';
  return (
    <div className="edit-section">
      <h2>{t('editCv.summary')}</h2>
      <div className="form-group">
        <label htmlFor="cv-summary">{t('editCv.summary')} *</label>
        <textarea
          id="cv-summary"
          value={summary}
          onChange={(e) => updateField('summary', e.target.value)}
          placeholder={t('editCv.summaryPlaceholder')}
          rows={6}
        />
        <div className="char-count">
          {summary.length} {t('editCv.characters')}
        </div>
      </div>
    </div>
  );
};

/**
 * Up / down / remove controls for a list entry.
 */
const EntryReorderBar = ({
  index,
  total,
  onMove,
  onRemove,
  moveUpLabel,
  moveDownLabel,
  removeLabel,
  canRemove,
}) => (
  <div className="entry-item__actions">
    {total > 1 ? (
      <div className="entry-item__reorder" role="group">
        <button
          type="button"
          className="entry-item__move"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          aria-label={moveUpLabel}
          title={moveUpLabel}
        >
          ↑
        </button>
        <button
          type="button"
          className="entry-item__move"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          aria-label={moveDownLabel}
          title={moveDownLabel}
        >
          ↓
        </button>
      </div>
    ) : null}
    {canRemove ? (
      <button
        type="button"
        className="btn btn-danger"
        onClick={() => onRemove(index)}
        aria-label={removeLabel}
      >
        {removeLabel}
      </button>
    ) : null}
  </div>
);

EntryReorderBar.propTypes = {
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  moveUpLabel: PropTypes.string.isRequired,
  moveDownLabel: PropTypes.string.isRequired,
  removeLabel: PropTypes.string.isRequired,
  canRemove: PropTypes.bool,
};

/**
 * Full-width add control, used at the bottom of entry lists and in empty states.
 */
const AddEntryButton = ({ onClick, label, empty }) => (
  <button
    type="button"
    className={`entry-add${empty ? ' entry-add--empty' : ''}`}
    onClick={onClick}
  >
    <span className="entry-add__plus" aria-hidden="true">+</span>
    {label}
  </button>
);

AddEntryButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  empty: PropTypes.bool,
};

/**
 * Tag chip with optional up/down reorder.
 */
const ReorderableTag = ({
  label,
  index,
  total,
  onMove,
  onRemove,
  moveUpLabel,
  moveDownLabel,
  removeLabel,
}) => (
  <div className="skill-tag">
    {total > 1 ? (
      <span className="skill-tag__reorder">
        <button
          type="button"
          className="skill-tag__move"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          aria-label={moveUpLabel}
          title={moveUpLabel}
        >
          ↑
        </button>
        <button
          type="button"
          className="skill-tag__move"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          aria-label={moveDownLabel}
          title={moveDownLabel}
        >
          ↓
        </button>
      </span>
    ) : null}
    <span>{label}</span>
    <button type="button" onClick={() => onRemove(index)} aria-label={removeLabel}>
      ×
    </button>
  </div>
);

ReorderableTag.propTypes = {
  label: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  moveUpLabel: PropTypes.string.isRequired,
  moveDownLabel: PropTypes.string.isRequired,
  removeLabel: PropTypes.string.isRequired,
};

/**
 * Experience Editor component.
 */
const ExperienceEditor = ({ experience, updateExperience, addExperience, removeExperience, moveExperience }) => {
  const { t } = useLanguage();
  const experienceList = Array.isArray(experience) ? experience : [];

  return (
    <div className="edit-section">
      <div className="section-header">
        <h2>{t('editCv.experience')}</h2>
        {experienceList.length > 0 ? (
          <button type="button" className="btn btn-primary" onClick={addExperience}>
            {t('editCv.addExperience')}
          </button>
        ) : null}
      </div>
      {experienceList.length > 1 ? (
        <p className="entry-reorder-hint">{t('editCv.reorderHint')}</p>
      ) : null}
      {experienceList.length === 0 ? (
        <div className="entry-empty">
          <p>{t('editCv.emptyExperience')}</p>
          <AddEntryButton onClick={addExperience} label={t('editCv.addExperience')} empty />
        </div>
      ) : (
        <>
        {experienceList.map((exp, index) => (
        <div key={`exp-${index}-${exp.company || ''}`} className="entry-item">
          <div className="item-header">
            <h3>{t('editCv.experienceItem', { n: index + 1 })}</h3>
            <EntryReorderBar
              index={index}
              total={experienceList.length}
              onMove={moveExperience}
              onRemove={removeExperience}
              moveUpLabel={t('editCv.moveUp')}
              moveDownLabel={t('editCv.moveDown')}
              removeLabel={t('editCv.removeEntry')}
              canRemove={experienceList.length > 1}
            />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor={`exp-${index}-title`}>{t('editCv.expFieldTitle')} *</label>
              <input
                id={`exp-${index}-title`}
                type="text"
                value={exp.title || ''}
                onChange={(e) => updateExperience(index, 'title', e.target.value)}
                placeholder={t('editCv.placeholderTitle')}
              />
            </div>
            <div className="form-group">
              <label htmlFor={`exp-${index}-company`}>{t('editCv.expFieldCompany')} *</label>
              <input
                id={`exp-${index}-company`}
                type="text"
                value={exp.company || ''}
                onChange={(e) => updateExperience(index, 'company', e.target.value)}
                placeholder={t('editCv.expPlaceholderCompany')}
              />
            </div>
            <div className="form-group">
              <label htmlFor={`exp-${index}-duration`}>{t('editCv.expFieldDuration')}</label>
              <input
                id={`exp-${index}-duration`}
                type="text"
                value={exp.duration || ''}
                onChange={(e) => updateExperience(index, 'duration', e.target.value)}
                placeholder={t('editCv.expPlaceholderDuration')}
              />
            </div>
            <div className="form-group">
              <label htmlFor={`exp-${index}-location`}>{t('editCv.fieldLocation')}</label>
              <input
                id={`exp-${index}-location`}
                type="text"
                value={exp.location || ''}
                onChange={(e) => updateExperience(index, 'location', e.target.value)}
                placeholder={t('editCv.placeholderLocation')}
              />
            </div>
            <div className="form-group full-width">
              <label htmlFor={`exp-${index}-description`}>{t('editCv.expFieldDescription')} *</label>
              <textarea
                id={`exp-${index}-description`}
                value={exp.description || ''}
                onChange={(e) => updateExperience(index, 'description', e.target.value)}
                placeholder={t('editCv.expPlaceholderDescription')}
                rows={6}
              />
            </div>
          </div>
        </div>
        ))
        }
        <AddEntryButton
          onClick={addExperience}
          label={t('editCv.addAnotherExperience')}
        />
      </>
      )}
    </div>
  );
};

/**
 * Skills Editor component.
 */
const SkillsEditor = ({ skills, updateSkills }) => {
  const { t } = useLanguage();
  const [skillInput, setSkillInput] = useState('');
  const currentSkills = Array.isArray(skills) ? skills : [];

  const handleAddSkill = () => {
    const trimmed = skillInput.trim();
    if (!trimmed) return;
    // Avoid exact duplicates (case-insensitive)
    if (currentSkills.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      setSkillInput('');
      return;
    }
    updateSkills([...currentSkills, trimmed]);
    setSkillInput('');
  };

  const handleRemoveSkill = (index) => {
    updateSkills(currentSkills.filter((_, i) => i !== index));
  };

  const handleMoveSkill = (fromIndex, toIndex) => {
    updateSkills(moveArrayItem(currentSkills, fromIndex, toIndex));
  };

  return (
    <div className="edit-section">
      <h2>{t('editCv.skills')}</h2>
      <div className="form-group">
        <label htmlFor="cv-skill-input">{t('editCv.addSkill')}</label>
        <div className="input-with-button">
          <input
            id="cv-skill-input"
            type="text"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSkill(); } }}
            placeholder={t('editCv.tagInputPlaceholder')}
          />
          <button type="button" className="btn btn-primary" onClick={handleAddSkill}>
            {t('editCv.addTag')}
          </button>
        </div>
      </div>
      {currentSkills.length > 1 ? (
        <p className="entry-reorder-hint">{t('editCv.reorderHint')}</p>
      ) : null}
      <div className="skills-list">
        {currentSkills.map((skill, index) => (
          <ReorderableTag
            key={`skill-${skill}-${index}`}
            label={skill}
            index={index}
            total={currentSkills.length}
            onMove={handleMoveSkill}
            onRemove={handleRemoveSkill}
            moveUpLabel={t('editCv.moveUp')}
            moveDownLabel={t('editCv.moveDown')}
            removeLabel={t('editCv.removeEntry')}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Education Editor component.
 */
const EducationEditor = ({ education, updateEducation, addEducation, removeEducation, moveEducation }) => {
  const { t } = useLanguage();
  const educationList = Array.isArray(education) ? education : [];

  return (
    <div className="edit-section">
      <div className="section-header">
        <h2>{t('editCv.education')}</h2>
        {educationList.length > 0 ? (
          <button type="button" className="btn btn-primary" onClick={addEducation}>
            {t('editCv.addEducation')}
          </button>
        ) : null}
      </div>
      {educationList.length > 1 ? (
        <p className="entry-reorder-hint">{t('editCv.reorderHint')}</p>
      ) : null}
      {educationList.length === 0 ? (
        <div className="entry-empty">
          <p>{t('editCv.emptyEducation')}</p>
          <AddEntryButton onClick={addEducation} label={t('editCv.addEducation')} empty />
        </div>
      ) : (
        <>
          {educationList.map((edu, index) => (
            <div key={`edu-${index}-${edu.institution || ''}`} className="entry-item">
              <div className="item-header">
                <h3>{t('editCv.educationItem', { n: index + 1 })}</h3>
                <EntryReorderBar
                  index={index}
                  total={educationList.length}
                  onMove={moveEducation}
                  onRemove={removeEducation}
                  moveUpLabel={t('editCv.moveUp')}
                  moveDownLabel={t('editCv.moveDown')}
                  removeLabel={t('editCv.removeEntry')}
                  canRemove
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor={`edu-${index}-degree`}>Título / cualificación *</label>
                  <input
                    id={`edu-${index}-degree`}
                    type="text"
                    value={edu.degree || ''}
                    onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                    placeholder="Grado en Ingeniería Informática"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`edu-${index}-institution`}>Centro *</label>
                  <input
                    id={`edu-${index}-institution`}
                    type="text"
                    value={edu.institution || ''}
                    onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                    placeholder="Nombre de la universidad"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`edu-${index}-date`}>Fecha / duración</label>
                  <input
                    id={`edu-${index}-date`}
                    type="text"
                    value={edu.date || ''}
                    onChange={(e) => updateEducation(index, 'date', e.target.value)}
                    placeholder="2020 - 2024 o Graduado en 2020"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`edu-${index}-location`}>{t('editCv.fieldLocation')}</label>
                  <input
                    id={`edu-${index}-location`}
                    type="text"
                    value={edu.location || ''}
                    onChange={(e) => updateEducation(index, 'location', e.target.value)}
                    placeholder={t('editCv.placeholderLocation')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor={`edu-${index}-description`}>{t('editCv.eduFieldDescription')}</label>
                <textarea
                  id={`edu-${index}-description`}
                  value={edu.description || ''}
                  onChange={(e) => updateEducation(index, 'description', e.target.value)}
                  placeholder={t('editCv.eduPlaceholderDescription')}
                  rows={3}
                />
              </div>
            </div>
          ))}
          <AddEntryButton
            onClick={addEducation}
            label={t('editCv.addAnotherEducation')}
          />
        </>
      )}
    </div>
  );
};

/**
 * Languages Editor component.
 */
const LanguagesEditor = ({ languages, updateLanguages }) => {
  const { t } = useLanguage();
  const [languageInput, setLanguageInput] = useState('');
  const currentLanguages = Array.isArray(languages) ? languages : [];

  const handleAddLanguage = () => {
    const trimmed = languageInput.trim();
    if (!trimmed) return;
    // Avoid exact duplicates (case-insensitive)
    if (currentLanguages.some((l) => l.toLowerCase() === trimmed.toLowerCase())) {
      setLanguageInput('');
      return;
    }
    updateLanguages([...currentLanguages, trimmed]);
    setLanguageInput('');
  };

  const handleRemoveLanguage = (index) => {
    updateLanguages(currentLanguages.filter((_, i) => i !== index));
  };

  const handleMoveLanguage = (fromIndex, toIndex) => {
    updateLanguages(moveArrayItem(currentLanguages, fromIndex, toIndex));
  };

  return (
    <div className="edit-section">
      <h2>{t('editCv.languages')}</h2>
      <div className="form-group">
        <label htmlFor="cv-language-input">{t('editCv.addLanguage')}</label>
        <div className="input-with-button">
          <input
            id="cv-language-input"
            type="text"
            value={languageInput}
            onChange={(e) => setLanguageInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddLanguage(); } }}
            placeholder={t('editCv.tagInputPlaceholder')}
          />
          <button type="button" className="btn btn-primary" onClick={handleAddLanguage}>
            {t('editCv.addTag')}
          </button>
        </div>
      </div>
      {currentLanguages.length > 1 ? (
        <p className="entry-reorder-hint">{t('editCv.reorderHint')}</p>
      ) : null}
      <div className="skills-list">
        {currentLanguages.map((language, index) => (
          <ReorderableTag
            key={`lang-${language}-${index}`}
            label={language}
            index={index}
            total={currentLanguages.length}
            onMove={handleMoveLanguage}
            onRemove={handleRemoveLanguage}
            moveUpLabel={t('editCv.moveUp')}
            moveDownLabel={t('editCv.moveDown')}
            removeLabel={t('editCv.removeEntry')}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Certifications Editor component.
 */
const CertificationsEditor = ({ certifications, updateCertifications }) => {
  const { t } = useLanguage();
  const [certInput, setCertInput] = useState('');
  const currentCerts = Array.isArray(certifications) ? certifications : [];

  const handleAddCert = () => {
    const trimmed = certInput.trim();
    if (!trimmed) return;
    updateCertifications([...currentCerts, trimmed]);
    setCertInput('');
  };

  const handleRemoveCert = (index) => {
    updateCertifications(currentCerts.filter((_, i) => i !== index));
  };

  const handleMoveCert = (fromIndex, toIndex) => {
    updateCertifications(moveArrayItem(currentCerts, fromIndex, toIndex));
  };

  return (
    <div className="edit-section">
      <h2>{t('editCv.certifications')}</h2>
      <div className="form-group">
        <label htmlFor="cv-cert-input">{t('editCv.addCertification')}</label>
        <div className="input-with-button">
          <input
            id="cv-cert-input"
            type="text"
            value={certInput}
            onChange={(e) => setCertInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCert(); } }}
            placeholder={t('editCv.tagInputPlaceholder')}
          />
          <button type="button" className="btn btn-primary" onClick={handleAddCert}>
            {t('editCv.addTag')}
          </button>
        </div>
      </div>
      {currentCerts.length > 1 ? (
        <p className="entry-reorder-hint">{t('editCv.reorderHint')}</p>
      ) : null}
      <div className="skills-list">
        {currentCerts.map((cert, index) => (
          <ReorderableTag
            key={`cert-${index}-${cert.slice(0, 20)}`}
            label={cert}
            index={index}
            total={currentCerts.length}
            onMove={handleMoveCert}
            onRemove={handleRemoveCert}
            moveUpLabel={t('editCv.moveUp')}
            moveDownLabel={t('editCv.moveDown')}
            removeLabel={t('editCv.removeEntry')}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Achievements Editor component.
 *
 * Each achievement is an independent input row — avoids the split/join
 * corruption that occurred when an entry contained internal newlines.
 */
const AchievementsEditor = ({ achievements, updateField }) => {
  const { t } = useLanguage();
  const [inputValue, setInputValue] = useState('');
  const currentList = Array.isArray(achievements) ? achievements : [];

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    updateField('achievements', [...currentList, trimmed]);
    setInputValue('');
  };

  const handleUpdate = (index, value) => {
    const next = [...currentList];
    next[index] = value;
    updateField('achievements', next);
  };

  const handleRemove = (index) => {
    updateField('achievements', currentList.filter((_, i) => i !== index));
  };

  const handleMove = (fromIndex, toIndex) => {
    updateField('achievements', moveArrayItem(currentList, fromIndex, toIndex));
  };

  return (
    <div className="edit-section">
      <h2>{t('editCv.achievements')}</h2>

      {/* Existing achievements */}
      {currentList.length > 1 ? (
        <p className="entry-reorder-hint">{t('editCv.reorderHint')}</p>
      ) : null}
      {currentList.map((item, index) => (
        <div key={`achievement-${index}`} className="entry-item" style={{ padding: '0.65rem 0.85rem', marginBottom: '0.5rem' }}>
          <div className="entry-item__actions" style={{ marginBottom: '0.4rem' }}>
            {currentList.length > 1 ? (
              <div className="entry-item__reorder" role="group">
                <button
                  type="button"
                  className="entry-item__move"
                  disabled={index === 0}
                  onClick={() => handleMove(index, index - 1)}
                  aria-label={t('editCv.moveUp')}
                  title={t('editCv.moveUp')}
                >↑</button>
                <button
                  type="button"
                  className="entry-item__move"
                  disabled={index === currentList.length - 1}
                  onClick={() => handleMove(index, index + 1)}
                  aria-label={t('editCv.moveDown')}
                  title={t('editCv.moveDown')}
                >↓</button>
              </div>
            ) : null}
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => handleRemove(index)}
              aria-label={t('editCv.removeEntry')}
            >
              {t('editCv.removeEntry')}
            </button>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <input
              type="text"
              value={item}
              onChange={(e) => handleUpdate(index, e.target.value)}
              placeholder={t('editCv.achievementPlaceholder')}
            />
          </div>
        </div>
      ))}

      {/* Add new achievement */}
      <div className="form-group" style={{ marginTop: currentList.length ? '0.75rem' : 0 }}>
        <label htmlFor="cv-achievement-input">{t('editCv.addAchievement')}</label>
        <div className="input-with-button">
          <input
            id="cv-achievement-input"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder={t('editCv.achievementPlaceholder')}
          />
          <button type="button" className="btn btn-primary" onClick={handleAdd}>
            {t('editCv.addTag')}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Preview Panel component for real-time CV preview.
 */
const PreviewPanel = ({ content }) => {
  const { t } = useLanguage();

  // Stable object URL for File photos — revoked on change / unmount.
  const objectUrlRef = useRef(null);
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (content.photo instanceof File) {
      const url = URL.createObjectURL(content.photo);
      objectUrlRef.current = url;
      setObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
        objectUrlRef.current = null;
      };
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);
    return undefined;
  }, [content.photo]);

  const photoSrc = resolvePhotoSrc(content.photo, objectUrl);

  return (
    <div className="preview-panel">
      <h2>{t('editCv.preview')}</h2>
      <div className="cv-preview">
        <div className="cv-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '1rem' }}>
            {photoSrc ? (
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '2px solid var(--color-border)',
                  flexShrink: 0
                }}>
                  <img
                    src={photoSrc}
                    alt={t('editCv.fieldPhoto')}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
            ) : null}
            <div style={{ flex: 1 }}>
              <h1>{content.name || t('editCv.placeholderName')}</h1>
              <p className="cv-title">{content.title || t('editCv.placeholderTitle')}</p>
            </div>
          </div>
          <div className="cv-contact">
            {content.email && <span>{content.email}</span>}
            {content.phone && <span>{content.phone}</span>}
            {content.location && <span>{content.location}</span>}
            {content.linkedin && <span>LinkedIn: {content.linkedin}</span>}
            {content.website && <span>{content.website}</span>}
            {content.github && <span>GitHub: {content.github}</span>}
          </div>
        </div>

        {content.summary && (
          <div className="cv-section">
            <h3>{t('editCv.summary')}</h3>
            <p>{content.summary}</p>
          </div>
        )}

        {content.experience && content.experience.length > 0 && (
          <div className="cv-section">
            <h3>{t('editCv.experience')}</h3>
            {content.experience.map((exp, index) => (
              <div key={`prev-exp-${index}`} className="experience-preview">
                <h4>{exp.title}</h4>
                <p className="company">
                  {exp.company}
                  {exp.location && ` | ${exp.location}`}
                  {exp.duration && ` | ${exp.duration}`}
                </p>
                {exp.description && (
                  <div className="description">
                    {exp.description.split('\n').map((line, i) => (
                      line.trim() ? <p key={i}>{line}</p> : null
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {content.skills && content.skills.length > 0 && (
          <div className="cv-section">
            <h3>{t('editCv.skills')}</h3>
            <p>{content.skills.join(' • ')}</p>
          </div>
        )}

        {content.education && (Array.isArray(content.education) ? content.education.length > 0 : content.education) && (
          <div className="cv-section">
            <h3>{t('editCv.education')}</h3>
            {Array.isArray(content.education) ? (
              content.education.map((edu, index) => (
                <div key={`prev-edu-${index}`} style={{ marginBottom: '1rem' }}>
                  <h4>{edu.degree}</h4>
                  <p style={{ margin: '0.25rem 0', color: 'var(--color-muted)' }}>
                    {edu.institution}
                    {edu.date && ` | ${edu.date}`}
                    {edu.location && ` | ${edu.location}`}
                  </p>
                  {edu.description && (
                    <p style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>{edu.description}</p>
                  )}
                </div>
              ))
            ) : (
              <p>{content.education}</p>
            )}
          </div>
        )}

        {content.languages && content.languages.length > 0 && (
          <div className="cv-section">
            <h3>{t('editCv.languages')}</h3>
            <p>{content.languages.join(' • ')}</p>
          </div>
        )}

        {content.certifications && content.certifications.length > 0 && (
          <div className="cv-section">
            <h3>{t('editCv.certifications')}</h3>
            <ul>
              {content.certifications.map((cert, index) => (
                <li key={`prev-cert-${index}`}>{cert}</li>
              ))}
            </ul>
          </div>
        )}

        {content.achievements && content.achievements.length > 0 && (
          <div className="cv-section">
            <h3>{t('editCv.achievements')}</h3>
            <ul>
              {content.achievements.map((achievement, index) => (
                <li key={`prev-ach-${index}`}>{achievement}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default CVEditDashboard;

